<script lang="ts">
  import { createEventDispatcher, onMount } from "svelte";
  import "carbon-components-svelte/css/all.css";
  import {
    Button,
    CodeSnippet,
    Column,
    Grid,
    LocalStorage,
    RadioButton,
    RadioButtonGroup,
    Row,
    Tile,
  } from "carbon-components-svelte";
  import Keyboard from "./Keyboard.svelte";
  import ArrowShiftDown16 from "carbon-icons-svelte/lib/ArrowShiftDown16";
  import Exit16 from "carbon-icons-svelte/lib/Exit16";

  export let core;
  export let params = {}; // to surpress the warning message from Router

  const dispatch = createEventDispatcher();

  onMount(() => {
    dispatch("routeEvent", { component: "TypeWriter" });
  });

  let text: string =
    ":alphabet-yellow-h::alphabet-white-e::alphabet-yellow-l::alphabet-white-l::alphabet-yellow-o:";
  $: preview = core.generate_preview_html(text);

  let qwertyKeys1: string[] = [
    "q",
    "w",
    "e",
    "r",
    "t",
    "y",
    "u",
    "i",
    "o",
    "p",
  ];
  let qwertyKeys2: string[] = ["a", "s", "d", "f", "g", "h", "j", "k", "l"];
  let qwertyKeys3: string[] = ["z", "x", "c", "v", "b", "n", "m"];
  let qwertyKeys4: string[] = ["exclamation", "at", "hash", "question"];
  let qwertyKeys: string[][] = [
    qwertyKeys1,
    qwertyKeys2,
    qwertyKeys3,
    qwertyKeys4,
  ];

  let alphabetKeys1: string[] = ["a", "b", "c", "d", "e", "f", "g", "h", "i"];
  let alphabetKeys2: string[] = ["j", "k", "l", "m", "n", "o", "p", "q", "r"];
  let alphabetKeys3: string[] = ["s", "t", "u", "v", "w", "x", "y", "z"];
  let alphabetKeys4: string[] = ["exclamation", "at", "hash", "question"];
  let alphabetKeys: string[][] = [
    alphabetKeys1,
    alphabetKeys2,
    alphabetKeys3,
    alphabetKeys4,
  ];
  let keys = qwertyKeys;
  let keyboardSelection = "qwerty";
  $: updateKeyboard(keyboardSelection);

  const updateKeyboard = (keyboardSelection: string) => {
    switch (keyboardSelection) {
      case "qwerty":
        keys = qwertyKeys;
        break;
      case "alphabet":
        keys = alphabetKeys;
        break;
    }
  };

  const idPattern = /(white|yellow)-(.+)/;

  const onKeyClick = (event) => {
    const id = findSrcElementID(event);
    const { color, key } = identifyKey(id);

    switch (key) {
      case "backspace":
        text = core.remove_last(text);
        break;
      case "enter":
        text = text + "\n";
        break;
      case "space":
        text = text + " ";
        break;
      default:
        text = append(text, key, color);
        break;
    }
  };

  const findSrcElementID = (event): string => {
    if (event.srcElement) {
      return event.srcElement.id;
    }
    if (event.detail.srcElement) {
      return event.detail.srcElement.id;
    }
    return "";
  };

  const identifyKey = (id: string): { color: string | null; key: string } => {
    switch (id) {
      case "enter":
      case "backspace":
      case "space":
        return { color: null, key: id };
    }

    const matched = idPattern.exec(id);
    if (matched.length != 3) {
      console.warn("unexpected id pattern:", id);
      return null;
    }
    return { color: matched[1], key: matched[2] };
  };

  const append = (text: string, key: string, color: string): string => {
    let c = key;
    switch (key) {
      case "at":
        c = "@";
        break;
      case "hash":
        c = "#";
        break;
      case "exclamation":
        c = "!";
        break;
      case "question":
        c = "?";
        break;
    }
    return text + core.convert_to_slack_alphabet_pack_emoji(c, color);
  };
</script>

<Grid>
  <Row>
    <Column md={6}>
      <p class="bx--label">Converted</p>
      <CodeSnippet type="multi" bind:code={text} style="max-width:100%" />
      <LocalStorage key="typewriter-text" bind:value={text} />
    </Column>
    <Column md={2}>
      <iframe
        title="advertisement"
        src="https://rcm-fe.amazon-adsystem.com/e/cm?o=9&p=9&l=ez&f=ifr&linkID=f1f66cfc0b10822984ab3bb43f425151&t=0xb17bea125-22&tracking_id=0xb17bea125-22"
        width="180"
        height="150"
        scrolling="no"
        border="0"
        marginwidth="0"
        style="border:none;"
        frameborder="0"
      />
    </Column>
  </Row>
  <Row>
    <Column>
      <div>&nbsp;</div>
    </Column>
  </Row>
  <Row>
    <Column>
      <p class="bx--label">Preview</p>
      <Tile>
        {#if preview}
          {@html preview}
        {/if}
      </Tile>
    </Column>
  </Row>
  <Row>
    <Column>
      <div>&nbsp;</div>
    </Column>
  </Row>
  <Row>
    <Column>
      <p class="bx--label">Virtual keyboard</p>
    </Column>
  </Row>
  <Row>
    <Column md={3}>
      <Tile>
        <Keyboard bind:keys color="yellow" on:keyClick={onKeyClick} />
      </Tile>
    </Column>
    <Column md={3}>
      <Tile>
        <Keyboard bind:keys color="white" on:keyClick={onKeyClick} />
      </Tile>
    </Column>
    <Column md={2}>
      <div class="special-key">
        <Button id="backspace" on:click={onKeyClick} icon={Exit16}
          >Backspace</Button
        >
      </div>
      <div class="special-key">
        <Button id="enter" on:click={onKeyClick} icon={ArrowShiftDown16}
          >Enter</Button
        >
      </div>
      <div class="special-key">
        <Button id="space" on:click={onKeyClick}>Space</Button>
      </div>
    </Column>
  </Row>
  <Row>
    <Column>
      <RadioButtonGroup bind:selected={keyboardSelection}>
        <RadioButton labelText="Qwerty" value="qwerty" />
        <RadioButton labelText="Alphabet" value="alphabet" />
      </RadioButtonGroup>
      <LocalStorage
        key="typewriter-keyboard-selection"
        bind:value={keyboardSelection}
      />
    </Column>
  </Row>
</Grid>

<style>
  div.special-key {
    margin-bottom: 4px;
  }
</style>
